<div class="share-certificates">
  <p class="description">
    Send all current certificates of one organisation in a single email. Each certificate gets its own
    download link, plus one link to download all of them as a ZIP. Links are valid for 30 days.
  </p>

  <!-- Organisation -->
  <div class="form-section">
    <label class="section-label" for="share-organisation">{{ labels.organisation }}</label>
    <app-shareable-organisation-select
      [kind]="kind"
      inputId="share-organisation"
      testId="share-organisation-select"
      (selectionChange)="onOrganisationChange($event)" />
  </div>

  @if (loadingPreview()) {
    <div class="loading-container">
      <p-progressspinner strokeWidth="4" [style]="{ width: '40px', height: '40px' }" />
      <span>Loading certificates...</span>
    </div>
  }

  @if (preview(); as p) {
    <p-divider />

    <!-- Certificates -->
    <div class="form-section">
      <label class="section-label">
        Certificates
        <span class="count" data-testid="share-certificate-count">{{ p.certificates.length }}</span>
      </label>
      @if (p.certificates.length === 0) {
        <div class="info-note warning">
          <i class="pi pi-exclamation-triangle"></i>
          <span>This organisation has no current certificates to share.</span>
        </div>
      } @else {
        <p-table
          [value]="p.certificates"
          [scrollable]="true"
          scrollHeight="220px"
          size="small"
          styleClass="certificates-table"
          dataKey="entityId"
          data-testid="share-certificates-table">
          <ng-template #header>
            <tr>
              <th>{{ labels.reference }}</th>
              <th>{{ labels.description }}</th>
              <th>{{ labels.category }}</th>
              <th>Valid until</th>
            </tr>
          </ng-template>
          <ng-template #body let-certificate>
            <tr>
              <td class="font-semibold white-space-nowrap">{{ certificate.reference }}</td>
              <td>{{ certificate.description || '—' }}</td>
              <td>{{ certificate.category || '—' }}</td>
              <td class="white-space-nowrap">
                {{ certificate.expiryDate ? (certificate.expiryDate | date: 'dd.MM.yyyy') : '—' }}
              </td>
            </tr>
          </ng-template>
        </p-table>
      }
    </div>

    <!-- Recipients -->
    <div class="form-section">
      <label class="section-label" for="share-contacts">Recipients</label>
      @if (p.contacts.length > 0) {
        <p-multiselect
          inputId="share-contacts"
          [options]="p.contacts"
          [ngModel]="selectedContacts()"
          (ngModelChange)="selectedContacts.set($event ?? [])"
          optionLabel="displayName"
          dataKey="id"
          placeholder="Choose contacts"
          display="chip"
          [showClear]="true"
          [filter]="true"
          styleClass="w-full"
          [appendTo]="'body'"
          data-testid="share-contacts-select">
          <ng-template let-contact #item>
            <div class="flex flex-column">
              <span>{{ contact.displayName }}</span>
              <small class="text-500">{{ contact.email }}</small>
            </div>
          </ng-template>
        </p-multiselect>
        <small class="text-500 block mt-1">Contacts tagged as certificate recipients are pre-selected.</small>
      } @else {
        <div class="info-note">
          <i class="pi pi-info-circle"></i>
          <span>This organisation has no contact with an email address. Add an address below.</span>
        </div>
      }
    </div>

    <!-- Additional email addresses -->
    <div class="form-section">
      <label class="section-label" for="share-additional-email">Additional email addresses</label>
      <div class="email-input">
        <input
          id="share-additional-email"
          type="email"
          pInputText
          [(ngModel)]="newEmail"
          (input)="onNewEmailInput()"
          (keydown.enter)="addEmail(); $event.preventDefault()"
          placeholder="name@example.com"
          class="w-full"
          data-testid="share-additional-email" />
        <p-button label="Add" icon="pi pi-plus" severity="secondary" [outlined]="true" (onClick)="addEmail()" />
      </div>
      @if (newEmailInvalid()) {
        <small class="text-red-500 block mt-1">Please enter a valid email address.</small>
      }
      @if (additionalEmails().length > 0) {
        <div class="email-chips">
          @for (email of additionalEmails(); track email) {
            <p-chip [label]="email" [removable]="true" (onRemove)="removeEmail(email)" />
          }
        </div>
      }
    </div>
  }

  <div class="actions">
    <p-button label="Cancel" severity="secondary" [text]="true" [disabled]="sending()" (onClick)="close()" />
    <p-button
      label="Send certificates"
      icon="pi pi-send"
      [loading]="sending()"
      [disabled]="!canSend()"
      (onClick)="send()"
      data-testid="share-send-button" />
  </div>
</div>
